import { ESTIMATE_MSG, GROUP_MSG, TEMPLATE_MSG, displayNameOf, ongoingNameOf, type CreateWarning, type Estimate, type EstimateCreate, type EstimateTemplateList, type EstimateTemplateSummary, type GroupDefault, type GroupDetail, type GroupList, type GroupListItem, type Partner, type ProjectChoice } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.jsx";
import { Choice, CreateActions, CreateBand, CrIcon, PreviewAside, PreviewMini, PreviewSheet, StepCard, stepStates } from "../components/Create.jsx";
import { DateInput } from "../components/DateInput.jsx";
import { ProjectPicker } from "../components/ProjectPicker.jsx";
import { useOngoing } from "../lib/ongoing.js";
import { PartnerChip, PartnerField, PartnerPickerDrawer, type PickedPartner } from "../components/PartnerPickerDrawer.jsx";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { BranchSelect, ContactSelect, defaultsOf, partyFieldsHelp } from "../components/GroupFields.jsx";
import { ErrorText, Field, Help } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { md, todayJst } from "../lib/num.js";

/** 紙の上で光らせる所（いま入れている欄に当たる所） */
type Part = "recipient" | "subject" | "staff";

/**
 * 新しい見積書（縦串2b → 039、docs/28 §4 → #96 作成画面の刷新、docs/36 §2）。
 *   上に藍の帯（‹ 戻り先・題・段）、左に段のカード（1 {customer} → 2 {project} → 3 中身）、右に「できあがりの紙」（入れたものがその場で写る）。
 *   段1：取引先検索（#288 から右のドロアー＝components/PartnerPickerDrawer.tsx。最近・よく使うもドロアーの中）→ 選んだら 1行のチップ＋支店・{customer}担当者
 *        （担当者・最近の行で選ぶと支店・担当者も一緒に入る）
 *   #289（docs/101 §6、Figma ③）：段1 の中を 1段目「{customer}｜{subject}」・2段目「{project}｜担当者｜見積日」に。{project}名の欄は無い
 *        （{project}の欄＝components/ProjectPicker.tsx。既定は GET /groups/default＝その{customer}の完了していない継続案件が1つならそれ・複数なら最後に使ったもの・無ければ「新規（{subject}と同じ）」）
 *   段2：作り方（白紙から／雛形から）
 *   必須は{customer}だけ（雛形からなら雛形も。ルール6.3）。「新規（{subject}と同じ）」のまま作ると{project}が同時にできる（名前＝{subject}）。
 *   既存の{project}を選ぶと、支店・{customer}担当者・担当者がその{project}の値になり、{subject}は「{project}名」か「{project}名（追加{job}）」になる（継続案件は{subject}を入れない）。
 *   ?client=（{customer}の詳細の「この{customer}に見積を作る」）・?group=（{project}から）で入った状態で開き、帯の「‹」と「やめる」はそこへ戻る。番号は出すときに付く
 */
export function EstimateNewPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const myName = me.data?.user.name ?? "";
  const company = me.data?.tenant.name ?? "";
  const [sp] = useSearchParams();
  const [error, setError] = useState<string | undefined>();
  const [from, setFrom] = useState<"blank" | "template">("blank");
  const [tpl, setTpl] = useState<EstimateTemplateSummary | null>(null);
  const templates = useQuery({ queryKey: ["templates"], queryFn: () => api<EstimateTemplateList>("GET", "/estimate-templates") });
  const tplItems = templates.data?.items ?? [];

  const entry = useRef({ client: sp.get("client"), group: sp.get("group") });
  const [clientId, setClientId] = useState<string | null>(sp.get("client"));
  const [partner, setPartner] = useState<Partner | null>(null);
  /** "new"＝新規（{subject}と同じ）・"ongoing:new"＝{ongoingProject}作成・それ以外は{project}の id（#289） */
  const [groupSel, setGroupSel] = useState<ProjectChoice>("new");
  const og = useOngoing();
  const [issueDate, setIssueDate] = useState(todayJst());
  const [branchId, setBranchId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const subjTouched = useRef(false);
  const [staff, setStaff] = useState("");
  const staffTouched = useRef(false);
  const subjRef = useRef<HTMLInputElement>(null);
  const [focus, setFocus] = useState<Part | null>(null);
  const [sheet, setSheet] = useState(false);
  /** 画面の中で{customer}を選んだか（入れかけの確かめに使う。?client=・?group= で入った分は数えない） */
  const [picked, setPicked] = useState(false);
  /** ドロアーで支店・担当者まで選んだ（{customer}の詳細が届いたら既定の代わりに入れる。#288） */
  const pickedParty = useRef<{ branchId: string | null; contactId: string | null } | null>(null);
  const [drawer, setDrawer] = useState(false);

  // ?group=：案件の顧客で開いて、その案件を選んでおく。帯の「‹」の行き先の名前にも使う
  const presetGroup = useRef(entry.current.group);
  const gq = useQuery({ queryKey: ["group", entry.current.group], queryFn: () => api<GroupDetail>("GET", `/groups/${entry.current.group}`), enabled: !!entry.current.group, retry: false });
  useEffect(() => {
    if (gq.data && !clientId && presetGroup.current) setClientId(gq.data.client.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gq.data]);
  // ?client=：戻り先の名前（選び直しても行き先は入ってきた{customer}のまま）
  const cq = useQuery({ queryKey: ["partner", entry.current.client], queryFn: () => api<Partner>("GET", `/partners/${entry.current.client}`), enabled: !!entry.current.client });

  const pq = useQuery({ queryKey: ["partner", clientId], queryFn: () => api<Partner>("GET", `/partners/${clientId}`), enabled: !!clientId });
  // その{customer}の{project}（#289：GET /groups?partner=。完了したものは出さない＝?group= で入った{project}だけは完了でも出す）と既定（fn_default_project）
  const groups = useQuery({ queryKey: ["groups", clientId, "choice"], queryFn: () => api<GroupList>("GET", `/groups?state=all&partner=${clientId}`), enabled: !!clientId });
  const dq = useQuery({ queryKey: ["groups-default", clientId], queryFn: () => api<GroupDefault>("GET", `/groups/default?partner=${clientId}`), enabled: !!clientId });
  const active = (groups.data?.items ?? []).filter((g) => !g.completedAt || g.id === entry.current.group);

  const pickNew = (p: Partner) => {
    setGroupSel("new");
    const d = defaultsOf(p);
    const party = pickedParty.current;
    setBranchId(party ? party.branchId ?? (party.contactId ? null : d.branchId) : d.branchId);
    setContactId(party ? party.contactId ?? (party.branchId ? null : d.contactId) : d.contactId);
    if (!staffTouched.current) setStaff(myName);
    if (!subjTouched.current) setSubject("");
  };
  const pickGroup = (g: GroupListItem) => {
    setGroupSel(g.id);
    const party = pickedParty.current;
    setBranchId(party ? party.branchId : g.partnerBranchId);
    setContactId(party ? party.contactId : g.partnerContactId);
    setStaff(g.staffName || myName);
    staffTouched.current = false;
    // 継続案件は{subject}を{project}名にしない（「見本商事 現場」は{subject}にならない。docs/101 §6）
    if (!subjTouched.current) setSubject(g.isOngoing && og.on ? "" : g.quoteCount ? `${g.name}（追加${L.job}）` : g.name);
  };
  /** {project}の欄で選んだ（#289）：既存の{project}・新規・{ongoingProject}作成 */
  const onProject = (v: ProjectChoice, g: GroupListItem | null) => {
    if (g) return pickGroup(g);
    if (partner) pickNew(partner);
    setGroupSel(v);
  };

  // 顧客の詳細が来たら既定を入れる（?group= なら、その案件を選ぶ）。
  // #179：その顧客の案件の一覧も届いてから（段2の案件のカードと段3を一度に出す。先に出すと、あとから入る案件のカードに件名・「見積作成」が押し下げられる）
  //   #289：既定（GET /groups/default）も届いてから＝既定の継続案件があとから欄に入って{subject}・担当者が動かないように
  const groupsSettled = (!!groups.data || groups.isError) && (!!dq.data || dq.isError);
  useEffect(() => {
    if (!pq.data || !groupsSettled || pq.data.id === partner?.id) return;
    setPartner(pq.data);
    pickNew(pq.data);
    const want = presetGroup.current ?? (dq.data && dq.data.choice !== "new" ? dq.data.choice : null);
    presetGroup.current = null;
    const g = want ? groups.data?.items.find((x) => x.id === want) : undefined;
    if (g) pickGroup(g);
    setTimeout(() => subjRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pq.data, groupsSettled]);
  // 担当者の既定はログインしている人の氏名（決めごと14）
  useEffect(() => {
    if (!staffTouched.current && groupSel === "new" && myName) setStaff((s) => s || myName);
  }, [myName, groupSel]);

  /** ドロアーで選んだ（#288）：{customer}を入れ替え、支店・担当者も選んでいればその値で */
  const onPicked = (c: PickedPartner) => {
    pickedParty.current = c.branch || c.contact ? { branchId: c.branch?.id ?? null, contactId: c.contact?.id ?? null } : null;
    setError(undefined);
    setPicked(true);
    if (c.id === clientId && partner) {
      if (pickedParty.current) pickNew(partner);
      return;
    }
    setPartner(null);
    setGroupSel("new");
    setClientId(c.id);
  };

  const create = useMutation({
    mutationFn: () => {
      const body: Partial<EstimateCreate> & { clientId: string } = {
        clientId: clientId!,
        projectId: groupSel,
        issueDate,
        partnerBranchId: branchId,
        partnerContactId: contactId,
        staffName: staff.trim(),
        subject: subject.trim(),
        fromTemplateId: from === "template" ? (tpl?.id ?? null) : null,
      };
      return api<Estimate & CreateWarning>("POST", "/estimates", body);
    },
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ["groups-default", e.client.id] });
      void qc.invalidateQueries({ queryKey: ["groups", e.client.id] });
      void qc.invalidateQueries({ queryKey: ["group-list"] });
      void qc.invalidateQueries({ queryKey: ["partners", "used"] });
      const msg = groupSel === "new" || groupSel === "ongoing:new" ? GROUP_MSG.estimateCreatedWithGroup : GROUP_MSG.estimateCreatedInGroup;
      // 完了した継続案件に作ったときは警告の文（★B＝止めない）
      nav(`/estimates/${e.id}`, { replace: true, state: { fresh: true, notice: e.warning ?? t(msg).replace("{name}", e.projectGroup?.name ?? "") } });
    },
    onError: (e) => setError(messageOf(e)),
  });
  const ready = !!partner && (from === "blank" || !!tpl);
  const submit = () => {
    if (!clientId) {
      setError(t(ESTIMATE_MSG.clientRequired));
      return;
    }
    if (ready && !create.isPending) create.mutate();
  };

  const pickFrom = (v: "blank" | "template") => {
    setFrom(v);
    setTpl(null);
    setError(undefined);
  };
  const ongoingName = partner ? ongoingNameOf(displayNameOf(partner), og.word) : "";

  // 段（docs/36 §1）：1 は{customer}を選んだら済（{project}は既定が入る。#289 で{project}の段を段1にまとめた）。2 は主役のボタンで終える段
  const steps = [
    { id: "en-step-client", label: L.customer },
    { id: "en-step-body", label: "中身" },
  ];
  const states = stepStates([!!partner, false]);
  const open = !!partner;
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ ?group= の{project}・?client= の{customer}・見積書の一覧。入れかけなら確かめる
  const dirty = picked || (subjTouched.current && !!subject.trim()) || (staffTouched.current && staff.trim() !== myName) || !!tpl;
  const back = useBackNav(
    entry.current.group
      ? { to: `/groups/${entry.current.group}`, label: gq.data?.name ?? L.project }
      : entry.current.client
        ? { to: `/partners/${entry.current.client}`, label: cq.data?.name ?? L.customer }
        : { to: "/estimates", label: "見積書" },
    dirty,
  );
  usePageName("新しい見積書");

  // 紙に写すもの
  const contact = partner?.contacts.find((c) => c.id === contactId) ?? null;
  const branch = partner?.branches.find((b) => b.id === branchId) ?? null;
  const hl: Part | null = focus ?? (!partner ? "recipient" : !subject.trim() ? "subject" : null);
  const contactText = contact ? `${contact.department ? `${contact.department} ` : ""}${contact.name}` : "";
  const recipient = partner ? recipientOf(partner.name, partner.honorific, branch?.name ?? "", contactText) : null;
  const paper = (
    <QuotePaper
      clientName={partner?.name ?? null}
      honorific={partner?.honorific ?? ""}
      branchName={branch?.name ?? ""}
      contact={contactText}
      subject={subject.trim()}
      staff={staff.trim()}
      company={company}
      template={from === "template" && tpl ? { title: tpl.title, lines: tpl.lineCount } : null}
      date={issueDate}
      hl={hl}
    />
  );
  const flow = (
    <div className="qpflow">
      <span className="chip ai">① この画面</span>
      <CrIcon k="right" />
      <span className="chip quiet">② 明細を書く</span>
      <CrIcon k="right" />
      <span className="chip quiet">③ 出す</span>
    </div>
  );

  return (
    <div className="crpage" data-testid="estimate-new">
      <div className="crin">
        <CreateBand back={back} title="新しい見積書" steps={steps} states={states} deco="house" />
        <div className="crgrid">
          <div className="crmain">
            <PreviewMini
              thumb={<MiniSheet />}
              line1={recipient ? [recipient.title, recipient.second].filter(Boolean).join(" ") : "できあがりの紙"}
              line2={partner ? subject.trim() : ""}
              open={sheet}
              onToggle={() => setSheet((v) => !v)}
              buttonLabel="紙面確認"
              sheetId="en-sheet"
            />
            <StepCard
              id={steps[0]!.id}
              n={1}
              title={L.customer}
              state={states[0]!}
              open
            >
              {/* 1段目（Figma ③）：{customer}｜{subject} */}
              <div className="crrow2">
                <div>
                  {clientId ? (
                    <PartnerChip
                      label={L.customer}
                      name={partner?.displayName ?? partner?.name ?? gq.data?.client.name ?? "…"}
                      branch={branch?.name ?? null}
                      contact={contact?.name ?? null}
                      onChange={() => setDrawer(true)}
                    />
                  ) : (
                    <PartnerField role="customer" label={L.customer} value={null} autoFocus onPick={onPicked} testId="client-field" />
                  )}
                </div>
                <Field id="en_subject" label={L.subject}>
                  <input
                    ref={subjRef}
                    id="en_subject"
                    className="inp"
                    autoComplete="off"
                    value={subject}
                    onChange={(e) => {
                      subjTouched.current = true;
                      setSubject(e.target.value);
                    }}
                    onFocus={() => setFocus("subject")}
                    onBlur={() => setFocus(null)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" || e.nativeEvent.isComposing || e.keyCode === 229) return;
                      e.preventDefault();
                      submit();
                    }}
                  />
                </Field>
              </div>
              <PartnerPickerDrawer open={drawer} role="customer" onClose={() => setDrawer(false)} onPick={onPicked} />
              {clientId ? (
                partner ? (
                  <>
                    <div className="crtwo">
                      <BranchSelect float partner={partner} id="en_branch" value={branchId} onChange={setBranchId} onFocus={() => setFocus("recipient")} onBlur={() => setFocus(null)} />
                      <ContactSelect float partner={partner} branchId={branchId} id="en_contact" value={contactId} onChange={setContactId} onPartner={setPartner} onFocus={() => setFocus("recipient")} onBlur={() => setFocus(null)} />
                    </div>
                    {/* 2段目（Figma ③）：{project}｜担当者｜見積日。{project}名の欄は無い（docs/101 §6） */}
                    <div className="crrow3" data-testid="group-pick">
                      <ProjectPicker groups={active} value={groupSel} onChange={onProject} ongoingName={ongoingName} subject={subject} />
                      <Field id="en_staff" label="担当者">
                        <input
                          id="en_staff"
                          className="inp"
                          autoComplete="off"
                          value={staff}
                          onChange={(e) => {
                            staffTouched.current = true;
                            setStaff(e.target.value);
                          }}
                          onFocus={() => setFocus("staff")}
                          onBlur={() => setFocus(null)}
                        />
                      </Field>
                      <Field id="en_date" label="見積日">
                        <DateInput id="en_date" label="見積日" clearable={false} value={issueDate} onChange={(v) => v && setIssueDate(v)} testId="en-issue-date" />
                      </Field>
                    </div>
                  </>
                ) : (
                  <p className="hint" style={{ margin: 0 }}>読み込んでいます…</p>
                )
              ) : null}
              {pq.isError ? <ErrorText>{messageOf(pq.error)}</ErrorText> : null}
              {gq.isError ? <ErrorText>{messageOf(gq.error)}</ErrorText> : null}
              {groups.isError ? <ErrorText>{messageOf(groups.error)}</ErrorText> : null}
            </StepCard>
            <StepCard
              id={steps[1]!.id}
              n={2}
              title="中身"
              state={states[1]!}
              open={open}
              help={
                <Help
                  testId="help-new"
                  label="新しい見積書の説明"
                  text={`${TEMPLATE_MSG.newHelp}会社名・住所・電話は、はじめて出すときにお聞きします。${partyFieldsHelp(L)}`}
                />
              }
            >
              <div className="fld">
                <span className="crsub" id="en_how">作り方</span>
                <div className="crchoices" role="radiogroup" aria-labelledby="en_how">
                  <Choice checked={from === "blank"} onClick={() => pickFrom("blank")} icon="file" square title="白紙から" testId="from-blank" />
                  <Choice checked={from === "template"} onClick={() => pickFrom("template")} icon="layers" square title={TEMPLATE_MSG.fromTemplate} sub={templates.data ? TEMPLATE_MSG.count(tplItems.length) : undefined} testId="from-template" />
                </div>
              </div>
              {from === "template" ? (
                tplItems.length ? (
                  <div className="fld">
                    <span className="crsub" id="en_tpl">{TEMPLATE_MSG.pickLabel}</span>
                    <div className="crchoices" role="radiogroup" aria-labelledby="en_tpl" data-testid="template-pick">
                      {tplItems.map((x) => (
                        <Choice key={x.id} checked={tpl?.id === x.id} onClick={() => setTpl(x)} icon="layers" title={x.title} sub={`${x.lineCount}行・${md(x.updatedAt.slice(0, 10))} 更新`} />
                      ))}
                    </div>
                  </div>
                ) : templates.data ? (
                  <p className="hint" style={{ margin: 0 }}>
                    {TEMPLATE_MSG.none}<Link to="/settings/templates">設定 → {TEMPLATE_MSG.listTitle}</Link> で作成できます。
                  </p>
                ) : templates.isError ? (
                  <ErrorText>{messageOf(templates.error)}</ErrorText>
                ) : (
                  <p className="hint" style={{ margin: 0 }}>{TEMPLATE_MSG.loading}</p>
                )
              ) : null}
            </StepCard>
            <ErrorText>{error}</ErrorText>
            <CreateActions back={back} label="見積作成" disabled={!ready || create.isPending} onClick={submit} testId="estimate-create" />
          </div>
          <PreviewAside title="できあがりの紙" icon="eye" chip="下書き">
            {paper}
            {flow}
          </PreviewAside>
        </div>
      </div>
      {back.dialog}
      {createPortal(
        <PreviewSheet id="en-sheet" open={sheet} onClose={() => setSheet(false)} title="できあがりの紙">
          {paper}
          {flow}
        </PreviewSheet>,
        document.body,
      )}
    </div>
  );
}

/** たたんだ形の小さな紙の絵（44×58） */
function MiniSheet() {
  return (
    <span className="crsheetmini">
      <i className="t" />
      <i />
      <i />
      <i className="s" />
      <i />
    </span>
  );
}

/**
 * 紙の宛名の2行（docs/28）：{customer}担当者を選んだ紙は「部署 氏名 様」（御中を付けない）。支店だけなら「支店名 御中」。
 * 右の紙とたたんだ形の1行目（#97）で同じ決まりを使う
 */
function recipientOf(clientName: string, honorific: string, branchName: string, contact: string): { title: string; second: string } {
  return {
    title: `${clientName}${contact || branchName ? "" : ` ${honorific}`}`,
    second: contact ? `${branchName ? `${branchName} ` : ""}${contact} 様` : branchName ? `${branchName} 御中` : "",
  };
}

/**
 * 右の紙（小さな見積書。docs/36 §2）。まだ入っていない所は灰の棒。いま入れている欄に当たる所（最初は宛名）は data-hl で淡い藍に光る。
 * 本物の紙（apps/api/src/pdf/quote-html.ts）と同じ並び：題（明朝・二重線）／宛名／日付・番号・自社名・担当／件名／御見積金額／明細／小計・消費税・合計
 */
function QuotePaper({
  clientName,
  honorific,
  branchName,
  contact,
  subject,
  staff,
  company,
  template,
  date,
  hl,
}: {
  clientName: string | null;
  honorific: string;
  branchName: string;
  contact: string;
  subject: string;
  staff: string;
  company: string;
  template: { title: string; lines: number } | null;
  /** 見積日（YYYY-MM-DD） */
  date: string;
  hl: Part | null;
}) {
  const part = (p: Part) => ({ "data-part": p, "data-hl": hl === p ? "" : undefined });
  const r = clientName ? recipientOf(clientName, honorific, branchName, contact) : null;
  const title = r?.title ?? null;
  const second = r?.second ?? "";
  return (
    <div className="qp" data-testid="quote-paper">
      <div className="qpt">御見積書</div>
      <div className="qph">
        <div className="qpto" {...part("recipient")} data-testid="paper-recipient">
          {title ? <div className="qpname">{title}</div> : <span className="gbar w70" />}
          {second ? <div className="qpsub">{second}</div> : null}
        </div>
        <div className="qpme">
          <div>{date ? `${Number(date.slice(0, 4))}年${Number(date.slice(5, 7))}月${Number(date.slice(8, 10))}日` : ""}</div>
          <div className="qsm">No. 未発行</div>
          {company ? <div className="co">{company}</div> : null}
          <div className="qsm" {...part("staff")}>
            担当　{staff || <span className="gbar w40" />}
          </div>
        </div>
      </div>
      <div className="qpsubj" {...part("subject")} data-testid="paper-subject">
        <span className="lbl">件名</span>
        {subject ? <span className="v">{subject}</span> : <span className="gbar w60" />}
      </div>
      <div className="qpamt">
        <span>御見積金額</span>
        <span className="v">¥ ―</span>
      </div>
      <div className="qptbl">
        <div className="qhd">
          <span>品名</span>
          <span>数量</span>
          <span>単価</span>
          <span>金額</span>
        </div>
        {[62, 48, 70, 40].map((w, i) => (
          <div key={i} className="qrow">
            <span className="gbar" style={{ width: `${w}%` }} />
            <span className="gbar" />
            <span className="gbar" />
            <span className="gbar" />
          </div>
        ))}
      </div>
      <p className="qpnote" data-testid="paper-note">
        <CrIcon k="pen" />
        {template ? TEMPLATE_MSG.paperNote(template.title, template.lines) : `明細は次の画面で書きます`}
      </p>
      <div className="qptot">
        {["小計", "消費税", "合計"].map((x) => (
          <div key={x}>
            <span>{x}</span>
            <span className="gbar" />
          </div>
        ))}
      </div>
    </div>
  );
}

